import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone,
  FileText,
  Clock,
  AlertTriangle,
  BrainCircuit,
  Sparkles,
  CheckCircle2,
  Bot,
  ArrowRight,
  PhoneOff,
  Loader2,
} from "lucide-react";

const staffTasks = [
  { icon: Phone, label: "Answering calls", person: "Front Desk Sarah", stress: "On hold 12 min" },
  { icon: FileText, label: "Verifying insurance", person: "Admin Lisa", stress: "3rd call today" },
  { icon: FileText, label: "Submitting claims", person: "Billing Mary", stress: "42 claims backed up" },
  { icon: Phone, label: "Calling patients", person: "Front Desk Sarah", stress: "17 no-shows to call" },
  { icon: AlertTriangle, label: "Chasing denials", person: "Billing Mary", stress: "28 denials pending" },
  { icon: FileText, label: "Posting EOBs", person: "Admin Lisa", stress: "Running behind" },
];

const aiAgents = [
  { name: "Amy", task: "Answered 47 calls today", status: "active" },
  { name: "Amanda", task: "Verified 23 patients", status: "active" },
  { name: "Ariel", task: "Submitted 42 claims", status: "complete" },
  { name: "Leena", task: "Filled 6 schedule gaps", status: "active" },
  { name: "Sarah", task: "Appealed 12 denials", status: "complete" },
  { name: "Alisha", task: "Posted 38 EOBs", status: "complete" },
  { name: "Stephanie", task: "Recalled 15 patients", status: "active" },
  { name: "Julia", task: "Collected $4,200 in A/R", status: "complete" },
  { name: "Kim", task: "Presented 8 treatment plans", status: "active" },
  { name: "Kianna", task: "Sent 19 patient statements", status: "complete" },
];

const AIClinicSlide = () => {
  const [tick, setTick] = useState(0);
  const [taskCount, setTaskCount] = useState(142);

  useEffect(() => {
    const t = setInterval(() => {
      setTick((p) => (p + 1) % aiAgents.length);
      setTaskCount((p) => p + Math.floor(Math.random() * 4) + 1);
    }, 2000);
    return () => clearInterval(t);
  }, []);

  const visibleAgents = [0, 1, 2, 3, 4, 5, 6].map(
    (offset) => aiAgents[(tick + offset) % aiAgents.length]
  );

  return (
    <div className="h-full w-full bg-[#0B1120] text-white flex flex-col relative overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute top-1/2 left-0 w-[600px] h-[600px] bg-red-900/10 rounded-full blur-[150px] -translate-x-1/3 -translate-y-1/2 pointer-events-none" />
      <div className="absolute top-1/2 right-0 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[150px] translate-x-1/3 -translate-y-1/2 pointer-events-none" />

      {/* Header */}
      <div className="text-center pt-6 pb-4 px-8 flex-shrink-0 relative z-10">
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-sm font-semibold mb-2 border border-blue-500/20">
            <Sparkles className="w-4 h-4" />
            <span>The Asha Transformation</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
            10 Softwares in 1 +{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-400">
              20 AI Employees to Do the Work Your Staff Does
            </span>
          </h2>
          <p className="text-sm text-slate-400 mt-2 max-w-3xl mx-auto leading-relaxed">
            We're the <span className="text-white font-semibold">only practice management platform</span> that doesn't just give you software — we give you{" "}
            <span className="text-blue-400 font-semibold">AI employees that actually use it for you</span>, so your staff doesn't have to.
          </p>
        </motion.div>
      </div>

      {/* Main split visual */}
      <div className="flex-1 flex items-stretch px-8 pb-6 gap-0 relative z-10 min-h-0">

        {/* LEFT: Traditional - Overwhelmed staff */}
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="flex-1 flex flex-col rounded-2xl rounded-r-none border border-slate-700/50 border-r-0 bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm overflow-hidden"
        >
          {/* Left header bar */}
          <div className="px-5 py-3 border-b border-slate-700/50 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center">
                <Clock className="w-4 h-4 text-slate-400" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-200">Without Asha</h3>
                <p className="text-[10px] text-slate-500 font-medium">Staff does everything manually</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-amber-500/10 border border-amber-500/20">
              <AlertTriangle className="w-3 h-3 text-amber-400" />
              <span className="text-[10px] font-bold text-amber-400">OVERWHELMED</span>
            </div>
          </div>

          {/* Animated stressed tasks */}
          <div className="flex-1 px-4 py-3 space-y-2 overflow-hidden">
            {staffTasks.map((task, i) => (
              <motion.div
                key={task.label}
                initial={{ opacity: 0, x: -15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + i * 0.08 }}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-slate-800/60 border border-slate-700/40 group"
              >
                <div className="w-8 h-8 rounded-lg bg-slate-700/60 flex items-center justify-center shrink-0">
                  <task.icon className="w-4 h-4 text-slate-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-300">{task.person}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 truncate">{task.label}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <motion.div
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
                  >
                    <Loader2 className="w-3 h-3 text-amber-400 animate-spin" />
                  </motion.div>
                  <span className="text-[10px] text-amber-400/80 font-medium">{task.stress}</span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Bottom bar */}
          <div className="px-5 py-3 border-t border-slate-700/50 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <PhoneOff className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-[10px] font-medium text-slate-500">Missed calls today: 12</span>
            </div>
            <span className="text-[10px] font-medium text-slate-500">Staff capacity: maxed</span>
          </div>
        </motion.div>

        {/* CENTER divider with arrow */}
        <div className="flex flex-col items-center justify-center z-20 -mx-5">
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, type: "spring" }}
            className="relative"
          >
            <div className="absolute -inset-3 bg-gradient-to-r from-slate-800 via-blue-600/30 to-slate-800 rounded-full blur-lg" />
            <div className="relative w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-xl shadow-blue-500/30 ring-4 ring-[#0B1120]">
              <ArrowRight className="w-5 h-5 text-white" />
            </div>
          </motion.div>
        </div>

        {/* RIGHT: AI-Powered Clinic */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="flex-1 flex flex-col rounded-2xl rounded-l-none border border-blue-500/30 border-l-0 bg-gradient-to-br from-slate-900/80 to-[#0d1530]/80 backdrop-blur-sm overflow-hidden relative"
        >
          {/* Subtle glow overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-500/5 pointer-events-none" />

          {/* Right header bar */}
          <div className="px-5 py-3 border-b border-blue-500/20 flex items-center justify-between flex-shrink-0 relative z-10">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <BrainCircuit className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white">With Asha</h3>
                <p className="text-[10px] text-blue-400 font-medium">16 AI employees working 24/7</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10px] font-bold text-emerald-400">ALL SYSTEMS LIVE</span>
            </div>
          </div>

          {/* Animated AI agent feed */}
          <div className="flex-1 px-4 py-3 space-y-2 overflow-hidden relative z-10">
            <AnimatePresence mode="popLayout">
              {visibleAgents.map((agent, i) => {
                const isTop = i === 0;
                return (
                  <motion.div
                    key={`${agent.name}-${i}-${tick}`}
                    initial={isTop ? { opacity: 0, y: -20, scale: 0.97 } : false}
                    animate={{ opacity: 1 - i * 0.08, y: 0, scale: 1 }}
                    transition={{ duration: 0.35 }}
                    layout
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all ${
                      isTop
                        ? "bg-blue-500/10 border-blue-500/30 shadow-sm shadow-blue-500/10"
                        : "bg-white/[0.03] border-white/5"
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        isTop
                          ? "bg-gradient-to-br from-blue-500 to-indigo-600 shadow-sm"
                          : "bg-slate-700/50"
                      }`}
                    >
                      <Bot className={`w-4 h-4 ${isTop ? "text-white" : "text-slate-400"}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-xs font-bold ${isTop ? "text-white" : "text-slate-400"}`}>
                        {agent.name}
                      </span>
                      <p className={`text-[11px] truncate ${isTop ? "text-blue-300" : "text-slate-500"}`}>
                        {agent.task}
                      </p>
                    </div>
                    <div className="shrink-0">
                      {agent.status === "complete" ? (
                        <CheckCircle2 className={`w-4 h-4 ${isTop ? "text-emerald-400" : "text-emerald-600/50"}`} />
                      ) : (
                        <motion.div
                          animate={{ opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.2 }}
                        >
                          <Sparkles className={`w-4 h-4 ${isTop ? "text-blue-400" : "text-blue-600/40"}`} />
                        </motion.div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          {/* Bottom bar */}
          <div className="px-5 py-3 border-t border-blue-500/20 flex items-center justify-between flex-shrink-0 relative z-10">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-[10px] font-bold text-emerald-400">0 missed calls</span>
            </div>
            <div className="flex items-center gap-2">
              <motion.span
                key={taskCount}
                initial={{ y: -8, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="text-[10px] font-bold text-blue-400 tabular-nums"
              >
                {taskCount} tasks completed today
              </motion.span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AIClinicSlide;
